/* Curated newsletter imagery. Native Shopify customer form remains the submit owner. */
(() => {
  class NewsletterSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.popup = this.closest('newsletter-popup');
      if (!this.popup) return;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.index = 0;
      this.generation = 0;
      this.failed = new Set();
      this.paused = false;
      this.interval = Math.max(1000, Number(this.dataset.interval) || 1200);
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = window.matchMedia('(max-width: 699px)');
      this.toggle = this.querySelector('[data-slideshow-toggle]');
      this.backdrop = this.closest('.shopify-section')?.querySelector('[data-newsletter-backdrop]');
      const listen = (target, name, handler) => target?.addEventListener(name, handler, {signal: this.abort.signal});
      listen(this.toggle, 'click', () => {
        this.paused = !this.paused;
        this.toggle.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
        this.toggle.setAttribute('aria-pressed', String(this.paused));
        this.sync();
      });
      listen(this.popup, 'focusin', event => { if (event.target.matches('input, textarea, select')) this.sync(); });
      listen(this.popup, 'focusout', () => queueMicrotask(() => this.abort && this.sync()));
      listen(document, 'visibilitychange', () => this.sync());
      listen(this.motion, 'change', () => this.sync());
      listen(this.mobile, 'change', () => this.sync());
      listen(window.visualViewport, 'resize', () => this.sync());
      listen(window.visualViewport, 'scroll', () => this.viewport());
      listen(this.backdrop, 'click', () => this.popup.hide());
      listen(this.popup, 'keydown', event => {
        if (event.key === 'Escape') this.popup.hide();
        if (event.key !== 'Tab' || !this.popup.hasAttribute('open')) return;
        const elements = [...this.popup.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href]')].filter(el => el.getClientRects().length);
        if (!elements.length) return;
        if (event.shiftKey && document.activeElement === elements[0]) { event.preventDefault(); elements.at(-1).focus(); }
        else if (!event.shiftKey && document.activeElement === elements.at(-1)) { event.preventDefault(); elements[0].focus(); }
      });
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.popup, {attributes:true, attributeFilter:['open']});
      this.sync();
    }
    viewport() {
      const viewport = window.visualViewport;
      const editing = this.popup.contains(document.activeElement) && document.activeElement?.matches('input, textarea');
      this.popup.classList.toggle('newsletter-popup--keyboard', Boolean(this.mobile.matches && editing && viewport && viewport.height < window.innerHeight * .8));
      if (viewport) {
        this.popup.style.setProperty('--newsletter-viewport-height', `${viewport.height}px`);
        this.popup.style.setProperty('--newsletter-viewport-top', `${viewport.offsetTop}px`);
      }
    }
    canPlay() {
      return this.popup.hasAttribute('open') && !document.hidden && !this.paused && !this.motion.matches && this.slides.length - this.failed.size > 1 && !(this.mobile.matches && this.hasAttribute('data-hide-mobile')) && !(this.popup.contains(document.activeElement) && document.activeElement?.matches('input, textarea, select'));
    }
    sync() {
      this.stop();
      this.viewport();
      const open = this.popup.hasAttribute('open');
      if (this.backdrop) this.backdrop.hidden = !open;
      const wasOpen = this.wasOpen;
      this.wasOpen = open;
      if (open && !wasOpen) {
        this.previousFocus = document.activeElement;
        this.popup.setAttribute('aria-modal', 'true');
        this.popup.querySelector('.pop-in__close-button')?.focus({preventScroll:true});
      } else if (!open && wasOpen) {
        this.popup.removeAttribute('aria-modal');
        if (this.previousFocus?.isConnected) this.previousFocus.focus({preventScroll:true});
      }
      if (this.toggle) this.toggle.hidden = this.slides.length < 2 || this.motion.matches;
      if (this.canPlay()) this.timer = setTimeout(() => this.advance(), this.interval);
    }
    stop() {
      clearTimeout(this.timer);
      this.timer = null;
      this.generation = (this.generation || 0) + 1;
    }
    async advance() {
      if (!this.canPlay()) return;
      const generation = this.generation;
      let next = (this.index + 1) % this.slides.length;
      while (this.failed.has(next) && next !== this.index) next = (next + 1) % this.slides.length;
      const slide = this.slides[next];
      const img = slide.querySelector('img');
      for (const source of slide.querySelectorAll('source[data-srcset]')) { source.srcset = source.dataset.srcset; source.removeAttribute('data-srcset'); }
      if (img.dataset.src) { img.loading = 'eager'; img.src = img.dataset.src; delete img.dataset.src; }
      try { await img.decode(); }
      catch (_) { this.failed.add(next); if (generation === this.generation) this.sync(); return; }
      if (!this.abort || generation !== this.generation || !this.canPlay()) return;
      this.slides[this.index].removeAttribute('data-active');
      this.slides[this.index].setAttribute('aria-hidden', 'true');
      slide.setAttribute('data-active', '');
      slide.setAttribute('aria-hidden', 'false');
      this.index = next;
      this.sync();
    }
    disconnectedCallback() {
      this.stop();
      this.observer?.disconnect();
      this.abort?.abort();
      this.abort = null;
      if (this.backdrop) this.backdrop.hidden = true;
    }
  }
  if (!customElements.get('huong-newsletter-slideshow')) customElements.define('huong-newsletter-slideshow', NewsletterSlideshow);
})();
